import type { HTMLAttributes, ReactNode } from 'react';
import { clsx } from '../clsx';
import { Eyebrow } from './Eyebrow';
import styles from './Metric.module.css';

export interface MetricProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  /** Formatted value ("498.000 €", "62 %"), set tabular in the display font. */
  value: ReactNode;
  /** Change or context under the value ("+12 % zur Vorwoche", "von 14"). */
  delta?: ReactNode | undefined;
  deltaTone?: 'neutral' | 'positive' | 'negative' | undefined;
  /** 34 px (statistics tiles) or 24 px (compact KPI strips). */
  size?: 'default' | 'compact' | undefined;
  /** Dark navy tile for the highlighted figure (Beratung 09 "Show-Quote"). */
  variant?: 'default' | 'dark' | 'plain' | undefined;
}

/** KPI tile: eyebrow label, large tabular value, delta line. */
export function Metric({
  label,
  value,
  delta,
  deltaTone = 'neutral',
  size = 'default',
  variant = 'default',
  className,
  ...rest
}: MetricProps) {
  return (
    <div
      className={clsx(
        styles.metric,
        variant === 'dark' && styles.dark,
        variant === 'plain' && styles.plain,
        size === 'compact' && styles.compact,
        className,
      )}
      {...rest}
    >
      <Eyebrow className={styles.label}>{label}</Eyebrow>
      <div className={styles.value}>{value}</div>
      {delta ? (
        <div
          className={clsx(
            styles.delta,
            deltaTone === 'positive' && styles.positive,
            deltaTone === 'negative' && styles.negative,
          )}
        >
          {delta}
        </div>
      ) : null}
    </div>
  );
}
